/* eslint-disable */
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ProductPanel from "./ProductPanel";
import ProductBreadcrumbs from "./ProductBreadcrumbs";
import "./ProductDetailComponent.css";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { useAppSelector } from "../../../store/hooks";
import { ProductDetailComponent as ProductDetailComponent_ } from "../interfaces/base";
import {
  extractProductRouteParamFromPathname,
  logErrorToBackend,
} from "../utilities/helpers";
import { matchesEditorTarget } from "../utilities/componentIdentity";
import styled from "@emotion/styled";
import ProductMetadata from "./ProductMetadata";
import RenderChildren from "../RenderChildren";
import usePageLanguage from "../hooks/usePageLanguage";
import React, { useEffect, useMemo } from "react";
import {
  getCatalogProductDisplayFields,
  getCatalogProductSlug,
} from "../utilities/catalogProductAdapter";
import {
  findCatalogProductByUuid,
  getCatalogEntry,
  getEffectivePrice,
  resolveDisplayCatalogProduct,
  resolveProductFromRouteParam,
} from "../utilities/productCatalog";
import {
  resolveHomepageRedirectUrl,
  shouldNavigateToProductNotFound,
} from "../utilities/redirectLogic";
import { formatDataLayerCategories } from "../utilities/dataLayerCategories";
import { formatDataLayerProductName } from "../utilities/dataLayerCategories";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

declare var API: any;

export default function ProductDetailComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: ProductDetailComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));

  const { products, productsLoaded, catalogProductsLoaded, catalogIndex } =
    useShoppingCart();
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams();
  const pages = useAppSelector((state) => state.builder.pages.list);
  const website = useAppSelector((state) => state.builder.website);
  const pageLanguages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );
  const locale = usePageLanguage();

  const isBuilder = !!document.getElementById("BuilderContent");
  const lang = usePageLanguage();

  const routeParam = useMemo(() => {
    const fromParams = routeParams.uuid ?? routeParams.id;
    if (fromParams) {
      return fromParams;
    }
    if (isBuilder) {
      return undefined;
    }
    return extractProductRouteParamFromPathname(
      location.pathname,
      pages,
      pageLanguages
    );
  }, [
    routeParams.uuid,
    routeParams.id,
    isBuilder,
    location.pathname,
    pages,
    pageLanguages,
  ]);

  const routeResolve = useMemo(() => {
    if (routeParam) {
      return resolveProductFromRouteParam(routeParam, products);
    }
    if (isBuilder && products.length > 0) {
      return { parent: products[0] };
    }
    return null;
  }, [routeParam, products, isBuilder]);

  const product = routeResolve?.parent ?? null;
  const initialAttributes = routeResolve?.initialAttributes;

  const displayProduct = useMemo(() => {
    if (!product) {
      return null;
    }
    if (initialAttributes) {
      return resolveDisplayCatalogProduct(product, products, initialAttributes);
    }
    const variantUuid = routeResolve?.variant?.productUuid;
    if (variantUuid) {
      return findCatalogProductByUuid(products, variantUuid) ?? product;
    }
    return product;
  }, [product, products, initialAttributes, routeResolve?.variant?.productUuid]);

  const displayFields = useMemo(() => {
    if (!displayProduct) {
      return null;
    }
    return getCatalogProductDisplayFields(displayProduct, lang);
  }, [displayProduct, lang]);

  useEffect(() => {
    if (!product || !displayFields) {
      return;
    }

    const sellableUuid =
      routeResolve?.variant?.productUuid ?? product.productUuid;
    const entry = getCatalogEntry(catalogIndex, sellableUuid);
    if (!entry) {
      return;
    }
    const { price, priceRegular } = getEffectivePrice(entry);
    const dataLayerProduct =
      routeResolve?.variant?.productUuid != null
        ? findCatalogProductByUuid(products, routeResolve.variant.productUuid) ??
          product
        : product;
    const productName = formatDataLayerProductName(dataLayerProduct.translations);
    const categories = formatDataLayerCategories(
      dataLayerProduct.productCategories ?? []
    );

    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: "view_item",
      ecommerce: {
        products_amount: Number(price),
        products_amount_net: Number(price),
        items: [
          {
            item_code: sellableUuid,
            item_name: productName,
            categories,
            price_regular: priceRegular,
            discount_amount: Number(priceRegular - price),
            quantity: 1,
          },
        ],
      },
    });
  }, [product, products, displayFields, routeResolve, catalogIndex, lang]);

  useEffect(() => {
    if (isBuilder || !product || !routeParam) {
      return;
    }

    const canonicalSegment =
      getCatalogProductSlug(product, locale) ?? product.productUuid;
    if (!canonicalSegment || canonicalSegment === routeParam) {
      return;
    }

    const pathParts = location.pathname.split("/");
    if (pathParts.length === 0) {
      return;
    }
    pathParts[pathParts.length - 1] = canonicalSegment;
    const nextPathname = pathParts.join("/");
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    const nextUrl = `${nextPathname}${location.search}${location.hash}`;
    if (currentUrl === nextUrl) {
      return;
    }

    navigate(nextUrl, { replace: true });
  }, [
    isBuilder,
    product,
    routeParam,
    locale,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  useEffect(() => {
    if (
      !shouldNavigateToProductNotFound({
        routeParam,
        product,
        productsLoaded,
        catalogProductsLoaded,
        isBuilder,
      })
    ) {
      return;
    }

    const productNotFoundData = {
      name: "ProductNotFound",
      message: "Product with id " + routeParam + " was not found in list.",
      stack:
        "Product with id " +
        routeParam +
        " was not found in list. In ProductDetailComponent.tsx",
      componentStack:
        "Product with id " +
        routeParam +
        " was not found in list. In ProductDetailComponent.tsx",
      href: window.location.href,
    };
    logErrorToBackend(productNotFoundData);
    const homepageRedirect = resolveHomepageRedirectUrl({
      pages,
      pageLanguages,
      website,
      navigatorLanguages: navigator.languages,
    });
    navigate(homepageRedirect ?? "/", { replace: true });
  }, [
    product,
    productsLoaded,
    catalogProductsLoaded,
    routeParam,
    isBuilder,
    pages,
    pageLanguages,
    website,
    navigate,
  ]);

  return (
    <Div
      id={id.toString()}
      key={id}
      data-style={attributes && attributes.styles}
      className={
        attributes &&
        attributes.classes +
          (attributes.classes != "container" &&
          attributes.classes != "container-fluid"
            ? " builder-component builder-component__product_detail"
            : "") +
          (matchesEditorTarget({ id, uuid }, editorId)
            ? " builder-component--active"
            : "")
      }
      data-component-type="section"
      style={{ height: "100%" }}
    >
      <ProductMetadata product={displayProduct} />
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />

      <div className="page light-gray-background-2 animsition withoutsidemenu product-page">
        {product && <ProductBreadcrumbs catalogProduct={product} />}
        {product && (
          <div className="page-content productsResults productPage">
            <ProductPanel
              product={product}
              id={id}
              initialAttributes={initialAttributes}
            />
          </div>
        )}
      </div>
    </Div>
  );
}
